// Enables critical path CSS rendering on the server
// https://github.com/kriasoft/isomorphic-style-loader

export type StyleRegistry = Set<string>;

type InsertCSS = (...styles: IsomorphicStyleModule[]) => void | (() => void);

/**
 * Creates a request-scoped style registry for server-side rendering. Each SSR
 * request should create its own registry to prevent concurrent requests from
 * interfering with each other's style collection.
 */
export const createStyleRegistry = (): StyleRegistry => new Set<string>();

// ---- Server Style Context Injection

/**
 * Injection point for the server-side CSS context provider. On the server,
 * `style-context.ts` calls this at import time to register a callback that
 * retrieves the current request-scoped `StyleRegistry` from `AsyncLocalStorage`.
 * On the client, this is never called and `serverRegistryProvider` stays `null`.
 */
let serverRegistryProvider: (() => StyleRegistry | undefined) | null = null;

export const initServerStyleContext = (
  provider: () => StyleRegistry | undefined,
): void => {
  serverRegistryProvider = provider;
};

/**
 * Server-side style registration. Resolves the current request-scoped style
 * registry dynamically at call time via the injected provider. This is a stable
 * module-level function reference so it works correctly as a `useMemo`
 * dependency in `StyleProvider`.
 */
const insertServerCSS: InsertCSS = (...styles) => {
  const registry = serverRegistryProvider?.();

  if (!registry) {
    throw new Error(
      'No style registry available. Ensure rendering is wrapped in runWithStyleRegistry.',
    );
  }

  styles.forEach(({ _getCss }) => registry.add(_getCss()));
};

/**
 * Client-side style insertion. Injects stylesheets into the document and
 * returns a cleanup function for removal on unmount.
 */
export const cleanupStyles: InsertCSS = (...styles) => {
  const removeCss = styles.map(({ _insertCss }) => _insertCss());

  return () => removeCss.forEach(dispose => dispose());
};

/**
 * Returns the appropriate `insertCss` function for the current environment.
 * On the server, returns a function that dynamically resolves the request-scoped
 * registry via `AsyncLocalStorage`. On the client, returns `cleanupStyles`.
 */
export const getInsertCSS = (): InsertCSS =>
  serverRegistryProvider ? insertServerCSS : cleanupStyles;
